import { useMemo } from 'react';
import { getInitDashboardOrWidgetFiltersFromWidgetParameters } from 'components/Dashboard/shared/utils';
import { isEqual } from 'lodash';
import dayjs from 'dayjs';
import { useDashboardContext } from 'screens/dashboard/Dashboard';
import { useWidgetContext } from '../Widget';
import type { DynamicFieldType } from 'components/DynamicFormField/DynamicFormField';
import { useIsCustomDashboard } from './useIsCustomDashboard';

export type AppliedFilter = {
  name: string;
  label: string;
  value: string | string[] | number | null;
  type: string;
};

export type AppliedFiltersResult = {
  filters: AppliedFilter[];
  count: number;
  unappliedFilterLabels: string[];
  appliedFilterLabels: string[];
};

const DEFAULT_APPLIED_FILTERS_RESULT: AppliedFiltersResult = {
  filters: [],
  count: 0,
  unappliedFilterLabels: [],
  appliedFilterLabels: [],
};

const hasValue = (value: any) => {
  return (
    value !== undefined &&
    value !== null &&
    value !== '' &&
    !(Array.isArray(value) && value.length === 0)
  );
};

const normalizeHardsetWidgetParameters = (
  hardsetWidgetParameters: Record<string, any>,
) => {
  const normalized = { ...(hardsetWidgetParameters || {}) };
  const startDate = normalized.startDate;
  const endDate = normalized.endDate;

  if (hasValue(startDate) && hasValue(endDate)) {
    normalized.dateRangeFilter = [startDate, endDate];
  }

  delete normalized.startDate;
  delete normalized.endDate;

  return normalized;
};

const getFormattedFilterValue = ({
  value,
  type,
}: {
  value: AppliedFilter['value'];
  type?: AppliedFilter['type'];
}) => {
  if (type === 'date' || (type === 'datetime' && typeof value === 'string')) {
    // Format date strings to a more readable format
    const date = dayjs(value as string);
    return !date.isValid() ? value : date.format('YYYY-MM-DD');
  }

  if (Array.isArray(value)) {
    // Join array values into a comma-separated string
    return value.join(', ');
  }

  return value;
};

export const compareDateFilters = (widgetValue: any, dashboardValue: any) => {
  if (!Array.isArray(widgetValue) || widgetValue.length !== 2) return false;
  const [startDate, endDate] = widgetValue;
  return (
    dayjs(startDate).isSame(dashboardValue[0], 'day') &&
    dayjs(endDate).isSame(dashboardValue[1], 'day')
  );
};

export const useWidgetFilters = () => {
  const { filters: dashboardFilters, dashboard } = useDashboardContext();
  const { widget, filters: widgetFilters, defaultModuleParameters } = useWidgetContext();
  const isCustomDashboard = useIsCustomDashboard();

  // These are the custom parameters that are hard-set for the widget and cannot be changed by dashboard filters.
  const hardsetWidgetParameters = useMemo(() => {
    return getInitDashboardOrWidgetFiltersFromWidgetParameters(widget);
  }, [widget]);
  const normalizedHardsetWidgetParameters = useMemo(
    () => normalizeHardsetWidgetParameters(hardsetWidgetParameters),
    [hardsetWidgetParameters],
  );

  const widgetAppliedFilters = useMemo((): AppliedFiltersResult => {
    // Only run for custom dashboards
    if (!isCustomDashboard) {
      return DEFAULT_APPLIED_FILTERS_RESULT;
    }

    if (!widget || !dashboardFilters || !dashboard?.filters) {
      return DEFAULT_APPLIED_FILTERS_RESULT;
    }

    // Get dashboard-level filters with their configurations
    const dashboardFilterConfigs = Array.isArray(dashboard.filters)
      ? dashboard.filters.filter((filter): filter is DynamicFieldType => !!filter?.name)
      : [];

    // Check if the dashboard has dashboard.filters defined.
    if (!dashboardFilterConfigs.length) {
      return DEFAULT_APPLIED_FILTERS_RESULT;
    }

    // Create a map of filter names to their configurations for easy lookup.
    const filterConfigMap = new Map<string, DynamicFieldType>();
    dashboardFilterConfigs.forEach((filter) => {
      filterConfigMap.set(filter.name, filter);
    });

    // Get filters that are different from dashboard filters
    const differingFilters: AppliedFilter[] = [];
    const unappliedFilterLabels: string[] = [];
    const appliedFilterLabels: string[] = [];

    // Combine widgetFilters and defaultModuleParameters to get all applied filters
    // widgetFilters takes precedence over defaultModuleParameters for any overlapping keys
    const filtersToUse = {
      ...(defaultModuleParameters || {}),
      ...(widgetFilters || {}),
    };

    const hardsetFilterNames = Object.keys(normalizedHardsetWidgetParameters).filter(
      (key) => filterConfigMap.has(key),
    );
    const hardsetFilterNameSet = new Set(hardsetFilterNames);

    // Loop through each dashboard filter and compare widget filter value to dashboard filter value
    dashboardFilterConfigs.forEach((filterConfig) => {
      const filterName = filterConfig.name;
      const filterValue = filtersToUse?.[filterName as keyof typeof filtersToUse];
      const dashboardValue =
        dashboardFilters[filterName as keyof typeof dashboardFilters];
      const hardsetValue =
        normalizedHardsetWidgetParameters[
          filterName as keyof typeof normalizedHardsetWidgetParameters
        ];
      const isHardsetFilter = hardsetFilterNameSet.has(filterName);

      // Check if dashboard has a non-empty value
      const dashboardHasValue = hasValue(dashboardValue);

      // Always include hardset widget parameters in unapplied list.
      if (isHardsetFilter) {
        const filterLabel = filterConfig.label || filterName;
        const formattedValue = getFormattedFilterValue({
          value: hardsetValue,
          type: filterConfig.type,
        });

        differingFilters.push({
          name: filterName,
          label: filterLabel,
          value: formattedValue,
          type: filterConfig.type || 'unknown',
        });

        unappliedFilterLabels.push(filterLabel);
        return;
      }

      // Skip filter if dashboard doesn't have a value for it and field is not hardset.
      if (!dashboardHasValue) {
        return;
      }

      let isDifferent = false;
      // TODO: add check here for dates widget.parameters = startDate and endDate -> needs to compare against [startDate, endDate].
      // TODO: add widget.parameters to the equation
      // TODO: if widget.parameter is defined, that means that specific filter is locked for the widget, and filter/parameter should be added to the filter messaging.

      const dashboardDateRangeFilter =
        Array.isArray(dashboardValue) && dashboardValue.length === 2;
      const isDateComparison =
        filterConfig.type === 'date' || filterConfig.type === 'datetime';

      if (isDateComparison && dashboardDateRangeFilter) {
        isDifferent = !compareDateFilters(filterValue, dashboardValue);
      } else {
        isDifferent = !isEqual(filterValue, dashboardValue);
      }

      // Use deep equality check for comparison, since values can be complex (arrays, objects).

      const filterLabel = filterConfig.label || filterName;

      // Only include filters that differ from dashboard filters
      if (isDifferent) {
        const formattedValue = getFormattedFilterValue({
          value: filterValue,
          type: filterConfig.type,
        });

        // Add to applied filters with metadata
        differingFilters.push({
          name: filterName,
          label: filterLabel,
          value: formattedValue,
          type: filterConfig.type || 'unknown',
        });

        unappliedFilterLabels.push(filterLabel);
      } else {
        appliedFilterLabels.push(filterLabel);
      }
    });

    return {
      filters: differingFilters,
      count: differingFilters.length,
      unappliedFilterLabels,
      appliedFilterLabels,
    };
  }, [
    widget,
    dashboardFilters,
    widgetFilters,
    dashboard,
    defaultModuleParameters,
    isCustomDashboard,
    normalizedHardsetWidgetParameters,
  ]);

  return {
    widgetAppliedFilters,
    // FIXME: placeholder until https://townnews.atlassian.net/browse/CMSADMIN-2110?focusedCommentId=372887 is addressed.
    // useIsWidgetLocked can be used here after above is completed.
    isWidgetLocked: false,
  };
};
